/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type { ActionType, AppItem, ClickResult, HostContext, ResultItem } from '../../../@types';
import ClickBase from '../ClickBase';
import { getLocalizedURL } from '@intl/adobe-locales';
import getConfig from '../../../config';
import { getLogger } from '../../../utils/SearchLogger';
import translatedStrings from '../../../../translations/en.json';
import { APP_TYPES } from '../../SearchResultsPage/SearchConstants';
import SearchUtils from '../../../utils/SearchUtils';
import AnalyticsUtils from '../../../analytics/AnalyticsUtil';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import UrlParse from 'url-parse';

/**
 * Constants
 */
const sourceToAnalyticsStringMap = {
	creativeCloud: 'app.search.synced_asset.title',

	/* eslint-disable-line camelcase */
	cloudDocs: 'app.search.cloud_docs.title',

	/* eslint-disable-line camelcase */
	libraries: 'app.search.libraries.title',
	lightroom: 'app.search.lightroom.title',
	mobileCreations: 'app.search.mobileCreations.title',

	/* eslint-disable-line camelcase */
	spark: 'app.search.ccexpress.title',
	xdArtifacts: 'app.search.xd.title',

	/* eslint-disable-line camelcase */
	ccFiles: 'app.search.synced_asset.title',
	localFiles: 'app.search.local_asset.title',
	stock: 'app.search.stock.title',
	helpx: 'app.search.support.title',
	libraryElement: 'app.search.libraries_element_asset.title',
	space: 'app.search.yourwork.spaces.title',
	canvas: 'app.search.yourwork.filter.type.canvas',
	spaceElement: 'app.search.yourwork.spaces.title',
};

class AssetClickBase<T, S> extends ClickBase<T, S> {
	/**
	 * Creates the 'clickResult' structure to pass into the onClick handler.
	 * @param {ResultItem} resultItem
	 * @param {string} appendedUrl
	 */
	createClickResult(
		resultItem: ResultItem,
		appendedUrl: string,
		action?: ActionType,
		openInAppName?: string,
	): ClickResult {
		// We don't want to directly modify the original image. Let's create a copy.
		const resultItemCopy = JSON.parse(JSON.stringify(resultItem));
		resultItemCopy.attributes.openUrl = appendedUrl;
		const clickResult: ClickResult = {
			action: 'open',
			item: resultItemCopy,
		};

		if (action) {
			clickResult.action = action;
		}

		if (openInAppName && openInAppName !== '') {
			clickResult.openInApp = openInAppName;
		}

		return clickResult;
	}

	/**
	 * genericOnClick method is the generic on click action when a user clicks on an asset.
	 * The file could be from the ccx-start.
	 * If that is the case for now, we open the file
	 * in Photoshop
	 * @param {HostContext} searchHostContext
	 * @param {ResultItem} resultItem
	 */
	genericOnClick(
		searchHostContext: HostContext,
		resultItem: ResultItem,
		action?: ActionType,
		app?: AppItem,
		options?: Record<string, any>,
	): void {
		const locale = searchHostContext.locale;
		const currentUrl = SearchUtils.getCurrentURL();
		const assetCategory = resultItem.attributes.assetCategory;
		let url = resultItem.attributes.openUrl;
		if (
			url &&
			searchHostContext.hostInfo.surface === 'CCDesktop' &&
			searchHostContext.hostInfo.client === 'CCDesktop'
		) {
			const URLJson = new URL(url);
			const acrobatURL = URLJson.hostname.includes('acrobat');
			url = acrobatURL ? url : this.appendRefererUrl(resultItem.attributes.openUrl, currentUrl);
		} else {
			url = this.appendRefererUrl(resultItem.attributes.openUrl, currentUrl);
		}

		const localizedUrl = getLocalizedURL(url, locale);
		AnalyticsUtils.location = options?.location ?? 'Search:asset:Your Work';
		AnalyticsUtils.locationVersion = AnalyticsUtils.searchComponentVersion;
		const appendedUrl = AnalyticsUtils.appendUrlWithAnalytics(localizedUrl);
		const analytics = new SearchAnalytics(searchHostContext);
		let analyticsString = sourceToAnalyticsStringMap[assetCategory];

		if (assetCategory === 'spark') {
			// Changes for Spark rebranding to CC Express
			analyticsString = SearchUtils.getTranslationIdForSparkRebranding(analyticsString);
		}

		let openAppName = app?.appName;
		if (resultItem.attributes.assetCategory === 'cloudDocs' && !action === true) {
			if (['spark', 'projectx'].includes(resultItem.attributes.assetSubCategory)) {
				openAppName = 'Adobe Express (Web)';
			} else if (searchHostContext.getMimeTypeToAppTable || searchHostContext.getMimeTypeToAppTable) {
				const mimeTypeToAppTable =
					searchHostContext.mimeTypeToAppTable || searchHostContext.getMimeTypeToAppTable?.(resultItem);
				const appItemArray = mimeTypeToAppTable[resultItem.attributes.mimeType];
				if (Array.isArray(appItemArray) && appItemArray.length > 0) {
					openAppName = appItemArray[0].appName;
				}
			}
		}
		const analyticsState = typeof options?.analytics === 'boolean' ? options?.analytics : true;
		analyticsState &&
			analytics.postClickOnResult({
				category: translatedStrings[analyticsString],
				data: resultItem,
				url: appendedUrl,
				modal: false,
				action,
				appName: openAppName,
				autocomplete: options?.autocomplete,
			});
		const clickResult: ClickResult = this.createClickResult(resultItem, appendedUrl, action, app?.appId);
		// Append the currentUrl as a referer to the parentUrl
		let appendedParentUrl = resultItem.attributes.parentUrl;
		if (
			appendedParentUrl &&
			searchHostContext.hostInfo.surface === 'CCDesktop' &&
			searchHostContext.hostInfo.client === 'CCDesktop'
		) {
			const URLJson = new URL(appendedParentUrl);
			const acrobatURL = URLJson.hostname.includes('acrobat');
			appendedParentUrl = acrobatURL
				? appendedParentUrl
				: this.appendRefererUrl(resultItem.attributes.parentUrl, currentUrl);
		} else {
			appendedParentUrl = this.appendRefererUrl(resultItem.attributes.parentUrl, currentUrl);
		}
		if (appendedParentUrl !== '') {
			const localizedParentUrl = getLocalizedURL(appendedParentUrl, locale);
			clickResult.item.attributes.parentUrl = localizedParentUrl;
		}

		// needed by CCD for local routing to libraries and library elements

		if (searchHostContext.hostInfo.surface === 'CCDesktop' && searchHostContext.hostInfo.client === 'CCDesktop') {
			if (clickResult.item.assetType === 'library') {
				clickResult.item.id = clickResult.item.attributes.libraryId;
			} else if (clickResult.item.assetType === 'libraryElement') {
				clickResult.item.id = clickResult.item.attributes.libraryElementId;
				clickResult.item.attributes.parentId = clickResult.item.attributes.libraryId;
			}
		}

		const type = clickResult.item.attributes.assetCategory;
		this.onClickCard(searchHostContext, type, clickResult);
	}

	/**
	 * Appends a referer url to a base url.
	 * @param {string} baseUrl
	 * @param {string} refererUrl
	 */
	appendRefererUrl(baseUrl: string | undefined, refererUrl: string | undefined) {
		let url = '';

		if (baseUrl && baseUrl !== undefined && baseUrl !== null) {
			const linkHref = baseUrl;
			const linkHrefUrl = new UrlParse(linkHref, true);
			// Get the queries of the sourceURL.
			const linkHrefUrlQueries = linkHrefUrl.query;
			// Get the queries and set the 'referer' query.
			linkHrefUrlQueries.referer = refererUrl;
			linkHrefUrl.set('query', linkHrefUrlQueries);
			url = linkHrefUrl.href;
		} else {
			getLogger().error('Your Work Utils', 'Base url does not exist for referer appending');
		}

		return url;
	}

	/**
	 * lightroomOnClick method is the generic on click action when a user clicks on a Lightroom asset.
	 * @param {HostContext} searchHostContext
	 * @param {ResultItem} resultItem
	 */
	lightroomOnClick(searchHostContext: HostContext, resultItem: ResultItem, options?: Record<string, any>): void {
		if (resultItem.attributes.openUrl) {
			const url = getLocalizedURL(
				SearchUtils.lightRoomSourceURLFixer(searchHostContext.environment, resultItem.attributes.openUrl),
				searchHostContext.locale,
			);
			AnalyticsUtils.location = options?.location ?? 'Search:asset:Your Work';
			AnalyticsUtils.locationVersion = AnalyticsUtils.searchComponentVersion;
			const appendedUrl = AnalyticsUtils.appendUrlWithAnalytics(url);
			const analytics = new SearchAnalytics(searchHostContext);
			options?.analytics &&
				analytics.postClickOnResult({
					category: translatedStrings['app.search.lightroom.title'],
					data: resultItem,
					url: appendedUrl,
					modal: false,
					action: 'open',
					appName: 'Lightroom (Web)',
					autocomplete: options?.autocomplete,
				});
			const clickResult: ClickResult = this.createClickResult(resultItem, appendedUrl);
			const type = clickResult.item.attributes.assetCategory;
			this.onClickCard(searchHostContext, type, clickResult);
		}
	}

	/**
	 * sparkOnClick method is the generic on click action when a user clicks on a CCExpress asset (CCExpress template or CCExpress project).
	 * @param {HostContext} searchHostContext
	 * @param {ResultItem} resultItem
	 */
	sparkOnClick(searchHostContext: HostContext, resultItem: ResultItem, options?: Record<string, any>): void {
		let url = '';
		if (resultItem.attributes.openUrl && resultItem.attributes.mimeType) {
			url = resultItem.attributes.openUrl;
			AnalyticsUtils.location = options?.location ?? 'Search:asset:Your Work';
			AnalyticsUtils.locationVersion = AnalyticsUtils.searchComponentVersion;
			let appendedUrl = '';

			if (url) {
				appendedUrl = AnalyticsUtils.appendUrlWithAnalytics(url);
			}

			const analytics = new SearchAnalytics(searchHostContext);
			const translationId = SearchUtils.getTranslationIdForSparkRebranding('app.search.ccexpress.title');
			options?.analytics &&
				analytics.postClickOnResult({
					category: translatedStrings[translationId],
					data: resultItem,
					url: appendedUrl,
					modal: false,
					action: 'open',
					appName: 'Adobe Express (Web)',
					autocomplete: options?.autocomplete,
				});
			const clickResult: ClickResult = this.createClickResult(resultItem, appendedUrl);
			const type = clickResult.item.attributes.assetCategory;
			this.onClickCard(searchHostContext, type, clickResult);
		}
	}

	/**
	 * xdOnClick method is the generic on click action when a user clicks on an XD asset.
	 * @param {HostContext} searchHostContext
	 * @param {ResultItem} resultItem
	 */
	xdOnClick(searchHostContext: HostContext, resultItem: ResultItem, options?: Record<string, any>) {
		if (resultItem.attributes.openUrl) {
			const url = getLocalizedURL(resultItem.attributes.openUrl, searchHostContext.locale);
			AnalyticsUtils.location = options?.location ?? 'Search:asset:Your Work';
			AnalyticsUtils.locationVersion = AnalyticsUtils.searchComponentVersion;
			const appendedUrl = AnalyticsUtils.appendUrlWithAnalytics(url);
			const analytics = new SearchAnalytics(searchHostContext);
			options?.analytics &&
				analytics.postClickOnResult({
					category: translatedStrings['app.search.xd.title'],
					data: resultItem,
					url: appendedUrl,
					autocomplete: options?.autocomplete,
				});
			const clickResult: ClickResult = this.createClickResult(resultItem, appendedUrl);
			const type = clickResult.item.attributes.assetCategory;
			this.onClickCard(searchHostContext, type, clickResult);
		} else {
			getLogger().info('Search Your Work', 'Click XD Artifact:', 'openUrl unavailable');
		}
	}

	/**
	 *
	 * @param {HostContext} searchHostContext
	 * @param {ResultItem} resultItem
	 */
	localFilesOnClick(searchHostContext: HostContext, resultItem: ResultItem, options?: Record<string, any>) {
		if (resultItem.attributes.openUrl) {
			const url = resultItem.attributes.openUrl;
			const analytics = new SearchAnalytics(searchHostContext);
			options?.analytics &&
				analytics.postClickOnResult({
					category: translatedStrings['app.search.local_asset.title'],
					data: resultItem,
					url,
					autocomplete: options?.autocomplete,
				});
			const clickResult: ClickResult = this.createClickResult(resultItem, url);
			const type = clickResult.item.attributes.assetCategory;
			this.onClickCard(searchHostContext, type, clickResult);
		}
	}

	/**
	 * @param {*} image
	 */
	static getColorThemeOpenUrl(searchHostContext: HostContext, resultItem: ResultItem, locale: string) {
		let id = resultItem.id;
		const n = id.lastIndexOf(':');
		const beginString = id.substring(0, n);
		const endString = id.substring(n + 1, id.length);
		id = `${beginString}/${endString}`;
		resultItem.attributes.openUrl = getLocalizedURL(
			`${getConfig(searchHostContext.environment, 'CC_ASSETS_URL')}/libraries/${id}`,
			locale,
		);
	}

	/**
	 * All around on-click function for when user clicks on a your work asset.
	 */
	onClick(searchHostContext: HostContext, resultItem: ResultItem, options?: Record<string, any>) {
		AnalyticsUtils.location = options?.location ?? 'Search:asset:Your Work';
		AnalyticsUtils.locationVersion = AnalyticsUtils.searchComponentVersion;

		if (resultItem.attributes?.data?.type === 'colorTheme') {
			if (resultItem.attributes.openUrl === undefined) {
				AssetClickBase.getColorThemeOpenUrl(searchHostContext, resultItem, searchHostContext.locale);
			}
		}

		if (resultItem.attributes.openUrl) {
			const analyticsState = typeof options?.analytics === 'boolean' ? options?.analytics : true;
			switch (resultItem.attributes.assetCategory) {
				case 'ccFiles':
				case 'cloudDocs':
				case 'libraries':
				case 'mobileCreations':
				case 'space':
				case 'sharedForReview':
				case 'project':
					this.genericOnClick(searchHostContext, resultItem, null, null, { analytics: analyticsState });
					break;

				case 'lightroom':
					this.lightroomOnClick(searchHostContext, resultItem, { analytics: analyticsState });
					break;

				case 'spark':
					this.sparkOnClick(searchHostContext, resultItem, { analytics: analyticsState });
					break;

				case 'xdArtifacts':
					this.xdOnClick(searchHostContext, resultItem, { analytics: analyticsState });
					break;

				case 'localFiles':
					this.localFilesOnClick(searchHostContext, resultItem, { analytics: analyticsState });
					break;

				default:
					getLogger().log('Your Work', 'on click', resultItem.attributes.assetCategory);
			}
		}
	}

	/**
	 * Onclick method to open parent folder of asset.
	 */
	onClickFullPath(searchHostContext: HostContext, event: React.SyntheticEvent, path: string) {
		// NOTE: Clicking on the source path means the cell is also clicked,
		// Use stopPropagation to prevent the onCellClick behaviour of opening up the one up view.
		event.stopPropagation();
		this.onClickUrl(searchHostContext, APP_TYPES.CREATIVE_CLOUD, path);
	}
}

export default AssetClickBase;
